import React from 'react';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  UserGroupIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';

const AudienceList = ({
  audiences,
  searchTerm,
  setSearchTerm,
  onEdit,
  onDelete,
  onViewMembers,
  page,
  setPage,
}) => {
  const filteredAudiences = audiences.filter((audience) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      audience.name?.toLowerCase().includes(searchLower) ||
      audience.description?.toLowerCase().includes(searchLower)
    );
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'EMAIL':
        return <EnvelopeIcon className="h-4 w-4" />;
      case 'SMS':
        return <DevicePhoneMobileIcon className="h-4 w-4" />;
      case 'WHATSAPP':
        return <ChatBubbleLeftIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case 'EMAIL':
        return 'bg-blue-100 text-blue-800';
      case 'SMS':
        return 'bg-green-100 text-green-800';
      case 'WHATSAPP':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="Search audiences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Grid */}
      {filteredAudiences.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No audiences</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create an audience to group your members for campaigns.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAudiences.map((audience) => (
            <div
              key={audience.id}
              className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {audience.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {audience.description || 'No description'}
                    </p>
                  </div>
                  <span className={`ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getChannelColor(audience.channel)}`}>
                    {getChannelIcon(audience.channel)}
                    {audience.channel}
                  </span>
                </div>

                <div className="mt-4 flex items-center text-sm text-gray-500">
                  <UsersIcon className="h-5 w-5 mr-1.5" />
                  <span>{audience.currentSize || audience.audienceMembers?.length || 0} members</span>
                </div>

                <div className="mt-4 flex items-center justify-between pt-4 border-t">
                  <button
                    onClick={() => onViewMembers(audience)}
                    className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                  >
                    View Members
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(audience)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onDelete(audience.id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredAudiences.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{filteredAudiences.length}</span> audiences
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceList;



